import React, { useState, useEffect } from 'react';
import api from '../services/api';

const TenantSettings = () => {
  const [loading, setLoading] = useState(true);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [tenantsList, setTenantsList] = useState({});
  const [defaultDb, setDefaultDb] = useState({});
  const [saving, setSaving] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);

  // Form State
  const [domain, setDomain] = useState('');
  const [database, setDatabase] = useState('');
  const [host, setHost] = useState('localhost');
  const [port, setPort] = useState('3306');
  const [user, setUser] = useState('root');
  const [password, setPassword] = useState('');

  const showNotification = (type, text) => {
    setToastMsg({ type, text });
    setTimeout(() => setToastMsg(null), 4000);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [curRes, listRes] = await Promise.all([
        api.get('/tenants/current').catch(() => ({ data: { success: false } })),
        api.get('/tenants').catch(() => ({ data: { success: false } }))
      ]);

      if (curRes.data?.success) {
        setCurrentTenant(curRes.data);
      }
      if (listRes.data?.success) {
        setTenantsList(listRes.data.tenants || {});
        setDefaultDb(listRes.data.default || {});
      }
    } catch (err) {
      console.error('Error loading tenant settings:', err);
      showNotification('danger', 'Failed to load tenant database settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSaveTenant = async (e) => {
    e.preventDefault();
    if (!domain.trim() || !database.trim()) {
      showNotification('danger', 'Please enter Domain Name and Database Name.');
      return;
    }

    setSaving(true);
    try {
      const res = await api.post('/tenants', {
        domain: domain.trim(),
        database: database.trim(),
        host: host.trim() || 'localhost',
        port: port || 3306,
        user: user.trim() || 'root',
        password
      });

      if (res.data?.success) {
        showNotification('success', res.data.message || 'Tenant database mapping saved!');
        setDomain('');
        setDatabase('');
        setPassword('');
        fetchData();
      } else {
        throw new Error(res.data?.message || 'Failed to save mapping');
      }
    } catch (err) {
      showNotification('danger', err.response?.data?.message || err.message || 'Error saving tenant mapping.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTenant = async (domainKey) => {
    if (!window.confirm(`Are you sure you want to remove database mapping for domain '${domainKey}'?`)) {
      return;
    }

    try {
      const res = await api.delete(`/tenants/${encodeURIComponent(domainKey)}`);
      if (res.data?.success) {
        showNotification('success', res.data.message);
        fetchData();
      }
    } catch (err) {
      showNotification('danger', err.response?.data?.message || 'Failed to delete domain mapping.');
    }
  };

  if (loading) {
    return (
      <div className="card shadow-sm border-0 p-5 text-center">
        <div className="spinner-border text-primary mx-auto" role="status"></div>
        <div className="mt-3 text-muted fw-semibold">Loading domain database settings...</div>
      </div>
    );
  }

  return (
    <div className="tenant-settings-container">
      {toastMsg && (
        <div className={`alert alert-${toastMsg.type} alert-dismissible fade show shadow-sm mb-4`} role="alert">
          {toastMsg.text}
          <button type="button" className="btn-close" onClick={() => setToastMsg(null)}></button>
        </div>
      )}

      {/* CURRENT DOMAIN CONNECTION STATUS CARD */}
      <div className="card shadow-sm border-0 mb-4 bg-gradient text-dark border-start border-4 border-primary">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-primary fs-6">Active Request Context</span>
                {currentTenant?.connected && (
                  <span className="badge bg-success fs-6"><i className="bi bi-check-circle-fill me-1"></i> Connected</span>
                )}
              </div>
              <h4 className="fw-bold mt-2 mb-1">
                <i className="bi bi-globe me-2 text-primary"></i>
                Domain: <code className="text-primary">{currentTenant?.currentDomain || window.location.hostname}</code>
              </h4>
              <p className="text-muted small mb-0">
                Connected MySQL Database: <strong className="text-dark fs-6">{currentTenant?.database || 'dbfish_25_26'}</strong>
                {currentTenant?.version && <span className="ms-2 text-muted">(MySQL v{currentTenant.version})</span>}
              </p>
            </div>
            <button className="btn btn-outline-primary btn-sm rounded-pill px-3" onClick={fetchData}>
              <i className="bi bi-arrow-clockwise me-1"></i> Refresh Status
            </button>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* ADD/EDIT DOMAIN TENANT FORM */}
        <div className="col-12 col-lg-5">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-light py-3 border-0">
              <h6 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                <i className="bi bi-plus-circle-fill text-primary"></i>
                Add Domain Database Mapping (डोमेन जोडणी)
              </h6>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleSaveTenant}>
                <div className="mb-3">
                  <label className="form-label small fw-bold text-dark">Domain Name / Hostname (डोमेन नाव):</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. firm1.fishsoftware.com or *.mydomain.com"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    required
                  />
                  <div className="text-muted small mt-1">
                    Enter full domain or wildcard pattern (e.g. <code>store1.alibagfish.com</code>)
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-bold text-dark">MySQL Database Name (डेटाबेस नाव):</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. dbfish_firm1"
                    value={database}
                    onChange={(e) => setDatabase(e.target.value)}
                    required
                  />
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-8">
                    <label className="form-label small fw-semibold text-dark">Database Host:</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="localhost"
                      value={host}
                      onChange={(e) => setHost(e.target.value)}
                    />
                  </div>
                  <div className="col-4">
                    <label className="form-label small fw-semibold text-dark">Port:</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="3306"
                      value={port}
                      onChange={(e) => setPort(e.target.value)}
                    />
                  </div>
                </div>

                <div className="row g-2 mb-4">
                  <div className="col-6">
                    <label className="form-label small fw-semibold text-dark">DB User:</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="root"
                      value={user}
                      onChange={(e) => setUser(e.target.value)}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-semibold text-dark">DB Password:</label>
                    <input
                      type="password"
                      className="form-control"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary w-100 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                      जतन होत आहे...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-floppy-fill"></i> सेव्ह करा (Save Domain Mapping)
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* TENANT DOMAIN MAPPINGS TABLE */}
        <div className="col-12 col-lg-7">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-light py-3 border-0 d-flex justify-content-between align-items-center">
              <h6 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                <i className="bi bi-diagram-3-fill text-success"></i>
                Configured Domain Mappings (डोमेन मॅपिंग यादी)
              </h6>
              <span className="badge bg-secondary">{Object.keys(tenantsList).length} Configured</span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-4">Domain / Pattern</th>
                      <th>Database</th>
                      <th>Host / Port</th>
                      <th className="text-end pe-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Default Fallback Row */}
                    <tr className="table-active">
                      <td className="ps-4">
                        <span className="fw-bold text-dark">Default Fallback</span>
                        <div className="text-muted small">Matches localhost / unmapped domains</div>
                      </td>
                      <td>
                        <span className="badge bg-dark fs-6">{defaultDb.database || 'dbfish_25_26'}</span>
                      </td>
                      <td className="small text-muted">{defaultDb.host || 'localhost'}:{defaultDb.port || 3306}</td>
                      <td className="text-end pe-4">
                        <span className="badge bg-secondary">System Default</span>
                      </td>
                    </tr>

                    {/* Custom Domain Rows */}
                    {Object.keys(tenantsList).length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-4 text-muted small">
                          No custom tenant domain rules configured yet. Add your first domain rule using the form.
                        </td>
                      </tr>
                    ) : (
                      Object.entries(tenantsList).map(([domKey, tConfig]) => (
                        <tr key={domKey}>
                          <td className="ps-4">
                            <code className="fw-bold text-primary fs-6">{domKey}</code>
                          </td>
                          <td>
                            <span className="badge bg-primary fs-6">{tConfig.database}</span>
                          </td>
                          <td className="small text-muted">
                            {tConfig.host || 'localhost'}:{tConfig.port || 3306}
                          </td>
                          <td className="text-end pe-4">
                            <button
                              className="btn btn-outline-danger btn-sm rounded-pill px-3"
                              onClick={() => handleDeleteTenant(domKey)}
                              title="Delete mapping"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantSettings;
